"use client";

import { useEffect, useState } from "react";
import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import { site } from "@/lib/site";
import { POSTHOG_READY_EVENT } from "@/lib/analytics";
import {
  analyticsAllowed,
  hasConsentAnswer,
  onConsentChange,
} from "@/lib/consent";

// Third-party analytics only loads in production so local dev stays clean. Set
// NEXT_PUBLIC_CONSENT_FALLBACK=granted in .env.local to exercise PostHog (and
// A/B experiments) locally.
const analyticsEnabled =
  process.env.NODE_ENV === "production" ||
  process.env.NEXT_PUBLIC_CONSENT_FALLBACK?.trim() === "granted";

// Only these hosts may write into the production PostHog project. Preview and
// staging deploys run production builds too (new.trein-vertraging.nl, Vercel
// previews), and their events polluted production analytics (audit
// 2026-07-10). The consent fallback above deliberately bypasses this gate so
// local experiment testing keeps working - those sessions are explicit opt-in.
const ANALYTICS_HOSTS: string[] = [
  site.hosts.apex,
  site.hosts.www,
  site.hosts.paidVariant,
];

const isAnalyticsHost = () =>
  typeof window !== "undefined" &&
  (ANALYTICS_HOSTS.includes(window.location.hostname) ||
    process.env.NEXT_PUBLIC_CONSENT_FALLBACK?.trim() === "granted");

// Has posthog.init() already run? (posthog-js sets __loaded once initialised.)
function isLoaded(): boolean {
  return (posthog as unknown as { __loaded?: boolean }).__loaded === true;
}

// Provides the PostHog client to the React tree so feature-flag hooks work.
// Capturing starts on the first render: until the OneTrust CMP (injected via
// the deferred GTM) has an answer, PostHog runs with memory persistence, so
// every visit is counted while no identifier is written to the device. An
// answer that grants analytics switches to cookie persistence; one that
// declines opts out, which stores the refusal itself (posthog-js keeps its
// opt-out flag in localStorage, the same way OneTrust keeps the answer).
// Waiting for the answer lost ~40% of landing-page pageviews at the 09-01
// cutover, because most visitors leave before the CMP has even loaded. GTM does
// not gate the app's own PostHog, so we gate it here.
export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const [, setReady] = useState(false);

  useEffect(() => {
    if (!analyticsEnabled || !isAnalyticsHost()) return;

    // Anything that needed PostHog at page load has been waiting since mount:
    // consent arrives after it, so a one-shot attempt at mount always missed.
    // Announcing readiness here is what lets those callers deliver (see
    // POSTHOG_READY_EVENT and ExperimentExposure).
    const announceReady = () => {
      setReady(true);
      window.dispatchEvent(new Event(POSTHOG_READY_EVENT));
    };

    const apply = () => {
      if (!analyticsAllowed()) {
        if (isLoaded()) {
          posthog.opt_out_capturing();
          setReady(false);
        }
        return;
      }
      const persistence = hasConsentAnswer() ? "localStorage+cookie" : "memory";
      if (!isLoaded()) {
        posthog.init(site.analytics.posthogKey, {
          api_host: site.analytics.posthogHost,
          defaults: "2026-05-30",
          person_profiles: "identified_only",
          cross_subdomain_cookie: true,
          persistence,
          loaded: announceReady,
        });
      } else {
        posthog.set_config({ persistence });
        posthog.opt_in_capturing();
        announceReady();
      }
    };

    apply();
    return onConsentChange(apply);
  }, []);

  return <PHProvider client={posthog}>{children}</PHProvider>;
}
